
import React from 'react';
import { franc } from 'franc'; // Used to detect the language of the given text.
import { useTranslation } from 'react-i18next';
import { Box, Typography, TypographyProps } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { logEvent } from 'firebase/analytics';
import { AnalyticsNames } from '../../keys/analyticNames';
import { analytics } from '../../store/firebase';
import { useUser } from '../../store';
import { shallow } from 'zustand/shallow';
import history from '../../utility/history';


const translateText = async (params?: any) => {
    const apiKey = process.env.REACT_APP_API_KEY;
    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`;
      try {
        const response = await axios.post(url, params);
        try{
          logEvent(analytics, AnalyticsNames.buttons, {
              content_type: "translate text",
              item_id: "translate text", 
          })  
        }catch{}
        return { data: response.data.data.translations[0].translatedText };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          return { error: axiosError.response.data };
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }
}

interface TranslateComponentProps {
    text: string | Record<string, string>;
    translatedText: string | Record<string, string>;
    setTranslatedText: React.Dispatch<React.SetStateAction<string | Record<string, string> | null>>;
    textToDetect: string | null;
    bottom?: number;
    typographyProp?: TypographyProps
}

// The component for translating text.
const TranslateComponent: React.FC<TranslateComponentProps> = ({ text, translatedText, setTranslatedText, textToDetect, bottom, typographyProp}) => {

    const [ uid ] = useUser((state) => [state.currentUser.uid], shallow)
    const { i18n, t } = useTranslation(); // Retrieves translation strings.

    const [ loading, setLoading ] = React.useState(false); // State to track loading status.

    // Mappings between franc's language codes and standard language codes.
    type LanguageDataMappings = {
        [key: string]: string;
    };
    
    const languageDataMappings: LanguageDataMappings = {
        "eng": "en",
        "spa": "es",
        "tha": "th",
        "ind": "id",
        "cmn": "zh"
    };

    // Detects the language of the given text using franc and maps it to a standard language code.
    const detectLanguage = () => {
        if(!textToDetect) return null;
        const langCode = franc(textToDetect, {minLength: 2}); // Set a minimum length to try improving accuracy
        const code = languageDataMappings[langCode] || "en";
        return code;
    };

      
    const translateObject = async (obj: Record<string, string>): Promise<void> => {

        if(!uid){
          history.push("/login")
          return
        }

        try {
          setLoading(true); 
          const translatedObject: Record<string, string> = {};      
          for (const [key, value] of Object.entries(obj)) {
            const res = await translateText({
              q: value,
              source: "en",
              target: i18n.language,
              format: "text"
            });
      
            translatedObject[key] = res.data;
          }
      
          setTranslatedText(translatedObject);
        } catch (error) {
          console.error('error', error);
        } finally {
          setLoading(false);
        }
    };
      
      

    const translateString = async (text: string) => {
        if(!uid){
          history.push("/login")
          return
        }

        try {
          setLoading(true)
          const res = await translateText({
            q: text,
            source: "en", // Assuming source language is English.
            target: i18n.language, // Target language.
            format: "text" // Format of the text being translated.
          });
          const translatedText = res.data; // Extracting translated text from response.
          setTranslatedText(translatedText); // Updating state with translated text.
        } catch (error) {
          console.log('error', error); 
        } finally {
          setLoading(false);
        }
    }

    const translatePost = async () => {
        if(typeof text === 'string') {
            translateString(text)
        } else {
            translateObject(text)
        }
    }

    const handleClick = () => {
      if (loading) {
          setTranslatedText(null);
      } else if (translatedText) {
          setTranslatedText(null);
      } else {
          translatePost();
      }
  };

  const renderText = () => {
      if (loading) {
          return t("translating") + " ...";
      } else if (translatedText) {
          return t("originalPost");
      } else {
          return t("translatePost");
      }
  };

    if(!detectLanguage || detectLanguage() === i18n.language)

    return <></>
    
    // Render logic for showing translated or original text based on user action.
    else return (
      <Box marginBottom={bottom}>
      {text && (
          <Box marginBottom={bottom}>
              <Typography
                  onClick={handleClick}
                  variant="inherit"
                  sx={{ cursor: "pointer"}}
                  fontSize="inherit"
                  fontWeight="inherit"
                  fontStyle="normal"
                  {...typographyProp}
              >
                  {renderText()}
              </Typography>
          </Box>
      )}
  </Box>
    )
}

export default TranslateComponent;
